/**
 * Chasing missing timesheets — **the app does it, nobody does it by hand.**
 *
 * This module replaced `nudge.ts`, which built a `mailto:` URL and admitted so
 * in its own header. The screen said "Not submitted 9", offered "Email the 8",
 * and every Monday the owner opened his mail client and re-sent a message the
 * app already knew how to write. Harvest has sent that reminder automatically
 * for years, and it was the one thing it still did better than this screen.
 *
 * Now `POST /api/cron/timesheet-reminders` fires hourly, each organization
 * sends on its own day and hour in its own time zone, and the SERVER composes
 * the message (`backend/src/modules/time/reminder-email.ts`) — so the reminder
 * that goes out at 09:00 on Monday and the one an approver triggers from this
 * screen are the same string, land in the same log, and count as the same send.
 *
 * Everything below is a thin client over that. There is no message-building in
 * the browser any more, on purpose.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useActiveOrgId, type PeriodKind, type TimesheetCadence } from './api';

export interface ReminderSettings {
  enabled: boolean;
  /** ISO weekday: 1 = Monday … 7 = Sunday. */
  weekday: number;
  /** Local hour in `timeZone`, 0–23. */
  hour: number;
  timeZone: string;
  defaults: { enabled: boolean; weekday: number; hour: number; timeZone: string };
}

/** One person's open PERIOD the next run will chase, and whether it already has. */
export interface ReminderTarget {
  personId: string;
  name: string;
  email: string;
  periodKind: PeriodKind;
  periodStart: string;
  label: string;
  status: 'DRAFT' | 'REJECTED';
  hours: number;
  capacityHours: number | null;
  reviewNote: string | null;
  /** The sentence the email will use — so a manager sees what they will read. */
  gap: string;
  remindedAt: string | null;
  remindedOutcome: string | null;
}

export type ChasedCadence = Exclude<TimesheetCadence, 'NEVER'>;

export interface ReminderOverview {
  settings: ReminderSettings;
  /** "Monday 9:00 AM" — computed by the same function the cron obeys. */
  scheduleLabel: string;
  /** One sentence per frequency: when that kind of timesheet is chased. */
  cadenceSchedule: Record<ChasedCadence, string>;
  nextRunAt: string | null;
  lastRunAt: string | null;
  /**
   * Delivery reality. In development `mode` is `file`: messages are written to
   * `.mail/` and nothing is transmitted, and the screen says so ABOVE the
   * button — the same honesty rule the invoice composer follows.
   */
  email: { mode: 'live' | 'file' | 'off'; reason: string | null };
  /** How many periods back a chase reaches, per period kind. */
  chaseWindow: Record<PeriodKind, number>;
  /** Periods the next run would chase. */
  open: ReminderTarget[];
  remindedCount: number;
}

/** Which period of whose — the unit a reminder is about. */
export interface ReminderItem {
  personId: string;
  periodKind: PeriodKind;
  periodStart: string;
}

export interface ReminderSendResult extends ReminderItem {
  name: string;
  to: string;
  outcome: string;
  reason?: string;
}

export interface ReminderSendResponse {
  sent: ReminderSendResult[];
  emailMode: string;
  emailReason: string | null;
  overview: ReminderOverview;
}

/** Key a reminder log entry or a queue row by the period it is about. */
export function reminderKey(item: ReminderItem): string {
  return `${item.personId}|${item.periodKind}|${item.periodStart}`;
}

/** Approver-only (403 otherwise), so it is fetched only when the server says so. */
export function useReminders(enabled: boolean) {
  const orgId = useActiveOrgId();
  return useQuery({
    queryKey: ['time', orgId, 'reminders'],
    queryFn: () => api.get<ReminderOverview>('/time/reminders'),
    enabled: Boolean(orgId) && enabled,
    retry: false,
  });
}

export function useUpdateReminders() {
  const queryClient = useQueryClient();
  const orgId = useActiveOrgId();
  return useMutation({
    mutationFn: (patch: { enabled?: boolean; weekday?: number; hour?: number }) =>
      api.patch<ReminderOverview>('/time/reminders', patch),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['time', orgId, 'reminders'] }),
  });
}

export function useSendReminders() {
  const queryClient = useQueryClient();
  const orgId = useActiveOrgId();
  return useMutation({
    mutationFn: (input: { items: ReminderItem[] }) =>
      api.post<ReminderSendResponse>('/time/reminders/send', input),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['time', orgId] }),
  });
}

/**
 * How many whole periods back `periodStart` is from the period containing
 * `today` — the same arithmetic the server's chase window uses. Days are
 * counted on the calendar, which can only ever be stricter than a workday
 * count, so the screen never offers a send the server would refuse.
 */
export function periodsAgo(periodStart: string, kind: PeriodKind, today: string): number {
  const [y1, m1, d1] = periodStart.split('-').map(Number);
  const [y2, m2, d2] = today.split('-').map(Number);
  if (kind === 'MONTH') return (y2 - y1) * 12 + (m2 - m1);
  const days = Math.round((Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / 86_400_000);
  return kind === 'WEEK' ? Math.floor(days / 7) : days;
}

export const PERIOD_NOUN: Record<PeriodKind, string> = {
  DAY: 'day',
  WEEK: 'week',
  MONTH: 'month',
};

export const CADENCE_LABEL: Record<TimesheetCadence, string> = {
  DAILY: 'Daily',
  WEEKLY: 'Weekly',
  MONTHLY: 'Monthly',
  NEVER: 'No timesheet',
};

// ---------------------------------------------------------------------------
// Wording
// ---------------------------------------------------------------------------

export const WEEKDAY_OPTIONS = [
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
  { value: '7', label: 'Sunday' },
];

export const HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => ({
  value: String(h),
  label: `${h % 12 === 0 ? 12 : h % 12}:00 ${h < 12 ? 'AM' : 'PM'}`,
}));

/** "Mon Aug 17, 9:00 AM" in the ORGANIZATION's zone, never the browser's. */
export function formatNextRun(iso: string | null, timeZone: string): string | null {
  if (!iso) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  try {
    return at.toLocaleString('en-US', {
      timeZone,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return at.toLocaleString();
  }
}

/** "Sep 28, 9:02 AM" — when a reminder actually went, in the org's zone. */
export function formatSentAt(iso: string | null, timeZone: string): string | null {
  if (!iso) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  try {
    return at.toLocaleString('en-US', {
      timeZone,
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return at.toLocaleString();
  }
}

/**
 * A send outcome in the words a manager can act on. `filed` is the development
 * mode's honest answer: written to disk, NOT transmitted — never dressed up as
 * "sent", because an app claiming an email nobody received is worse than one
 * that says nothing.
 */
export function outcomeLabel(outcome: string): string {
  switch (outcome) {
    case 'sent':
      return 'sent';
    case 'filed':
      return 'written to .mail (not transmitted)';
    case 'refused':
      return 'refused by the email policy';
    case 'already-sent':
      return 'already reminded';
    case 'no-email':
      return 'no email address on file';
    default:
      return 'failed';
  }
}

/** Did anything actually leave the building (or at least get written down)? */
export function isDelivered(outcome: string | null): boolean {
  return outcome === 'sent' || outcome === 'filed';
}
